/**
 * features/finance/ControlAccounts.tsx
 * ------------------------------------------------------------------
 * Does the ledger agree with the sub-ledgers?
 *
 * The page a bursar should open first each morning once posting is live.
 * The general ledger says the school is owed P2.6m; the invoice list says
 * the same thing a different way. If those disagree, every report built on
 * either is wrong and nobody can tell which — so this is the check that
 * tells you whether to trust the rest of the section.
 *
 * A DIFFERENCE COMES WITH ITS EVIDENCE. Each failing control lists the
 * records contributing most to it and a sentence on what usually causes it.
 * "P4,000 out" sends somebody looking; "P4,000 out, and here are the three
 * invoices" is a morning's work.
 *
 * THE CONTROLS THIS SYSTEM CANNOT ANSWER YET ARE SHOWN, GREYED, with what
 * each is waiting for. Dropping them would let a reader believe the ledger
 * is fully reconciled when half of it is simply unexamined.
 * ------------------------------------------------------------------ */
import { useMemo, useState } from 'react';
import {
  ShieldCheck, AlertTriangle, CircleSlash, ChevronDown, ChevronRight, Info,
} from 'lucide-react';
import { useGlData } from '@/features/finance/useGlData';
import { useFinanceData } from '@/features/finance/useFinanceData';
import { FINANCE_COLLECTIONS } from '@/lib/financeCore';
import { collection, onSnapshot, query } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useEffect } from 'react';
import {
  buildControlReport, CONTROL_STATE_LABEL,
  type ControlCheck, type ControlDeferral,
} from '@/lib/controlAccounts';
import { deferralOutstanding, type Deferral } from '@/lib/recurring';
import { VOUCHER_COLLECTIONS, type Voucher } from '@/lib/vouchers';
import {
  PageHeader, SoftCard, PanelHeader, StatChip, btnGhost,
} from '@/features/finance/ui';
import { Spinner } from '@/components/ui/primitives';

const money = (n: number) =>
  n.toLocaleString('en-BW', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const TONE: Record<ControlCheck['state'], string> = {
  agrees: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  differs: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
  'not-in-service': 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
  'awaiting-module': 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
  'no-account': 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
};

export default function ControlAccounts() {
  const [fy, setFy] = useState(`FY${new Date().getFullYear()}`);
  const gl = useGlData(fy);
  const fin = useFinanceData();
  const [open, setOpen] = useState<string | null>(null);

  const [deferrals, setDeferrals] = useState<Deferral[]>([]);
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  useEffect(() => onSnapshot(
    query(collection(db, VOUCHER_COLLECTIONS.vouchers)),
    (snap) => setVouchers(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Voucher)),
    () => setVouchers([]),
  ), []);
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, FINANCE_COLLECTIONS.deferrals)),
      (snap) => setDeferrals(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Deferral)),
      () => setDeferrals([]),
    );
    return unsub;
  }, []);

  /* Outstanding is measured against what has actually posted, the same way
     the Period End screen measures it — two screens must not disagree about
     how much of a prepayment is left. */
  const postedDeferralSources = useMemo(() => {
    const s = new Set<string>();
    for (const jr of gl.journals) {
      if (jr.source?.type === 'deferral' && jr.source.id) s.add(jr.source.id);
    }
    return s;
  }, [gl.journals]);

  const controlDeferrals: ControlDeferral[] = useMemo(
    () => deferrals.map((d) => ({
      id: d.id,
      kind: d.kind,
      description: d.description,
      active: d.active,
      outstanding: deferralOutstanding(d, postedDeferralSources),
    })),
    [deferrals, postedDeferralSources],
  );

  /* IN SERVICE means the school has named a cut-over AND switched posting
     on. Before that the ledger is legitimately empty while the sub-ledgers
     hold millions, and reporting that as a discrepancy would be noise. */
  const ledgerInService = Boolean(
    fin.settings.glCutoverDate && fin.settings.glPostingEnabled,
  );

  const report = useMemo(
    () => buildControlReport({
      accounts: gl.accounts,
      journals: gl.journals,
      invoices: fin.invoices,
      deferrals: controlDeferrals,
      vouchers,
      ledgerInService,
    }),
    [gl.accounts, gl.journals, fin.invoices, controlDeferrals, vouchers, ledgerInService],
  );

  if (gl.loading || fin.loading) return <div className="p-8 flex justify-center"><Spinner /></div>;

  const years = Array.from({ length: 5 }, (_, i) => `FY${new Date().getFullYear() - i}`);

  return (
    <div className="space-y-5">
      <PageHeader
        icon={ShieldCheck}
        title="Control Accounts"
        subtitle="Whether the ledger and the sub-ledgers tell the same story"
        actions={(
          <select
            value={fy}
            onChange={(e) => setFy(e.target.value)}
            className="px-3 py-1.5 rounded-lg border-2 border-gray-200 dark:border-gray-600 dark:bg-gray-800 text-sm"
          >
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        )}
      />

      {report.ledgerNotInService ? (
        <div className="rounded-xl border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/60 px-4 py-3 flex items-start gap-2">
          <Info size={16} className="mt-0.5 shrink-0 text-gray-500" />
          <p className="text-sm text-gray-700 dark:text-gray-300">
            The ledger has not been brought into service. Nothing posts until the school names
            a cut-over date and switches posting on in Opening Balances, so these controls
            cannot be compared yet — the sub-ledger figures below are what they WILL be
            measured against.
          </p>
        </div>
      ) : (
        <div className={`rounded-xl border px-4 py-3 flex items-start gap-2 ${report.allAgree
          ? 'border-emerald-200 bg-emerald-50 dark:border-emerald-900/60 dark:bg-emerald-950/40'
          : 'border-red-200 bg-red-50 dark:border-red-900/60 dark:bg-red-950/40'}`}
        >
          {report.allAgree
            ? <ShieldCheck size={16} className="mt-0.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
            : <AlertTriangle size={16} className="mt-0.5 shrink-0 text-red-600 dark:text-red-400" />}
          <p className={`text-sm ${report.allAgree
            ? 'text-emerald-800 dark:text-emerald-200'
            : 'text-red-800 dark:text-red-200'}`}
          >
            {report.allAgree
              ? `Every control that can be checked agrees. ${report.unanswered} still awaits its module.`
              : `${report.differing} control${report.differing === 1 ? '' : 's'} out by `
                + `${money(report.totalDifference)} in total. Until that is found, a report built `
                + 'on either side may be wrong and there is no way to tell which.'}
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatChip icon={ShieldCheck} label="Agreeing" value={String(report.agreeing)} />
        <StatChip icon={AlertTriangle} label="Not agreeing" value={String(report.differing)} />
        <StatChip icon={CircleSlash} label="Nothing to compare yet" value={String(report.unanswered)} />
      </div>

      {report.checks.map((c) => {
        const expandable = c.contributors.length > 0;
        return (
          <SoftCard key={c.key} pad="p-0" hover={false}>
            <PanelHeader
              icon={c.state === 'differs' ? AlertTriangle : ShieldCheck}
              title={c.label}
              subtitle={c.accountCode ? `${c.accountCode} · ${c.accountName}` : 'No account carries this key'}
              right={(
                <span className={`text-xs px-2 py-0.5 rounded-full ${TONE[c.state]}`}>
                  {CONTROL_STATE_LABEL[c.state]}
                </span>
              )}
            />
            <div className="p-5 space-y-3">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Compared against {c.detailIs}.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Ledger says</p>
                  <p className="tabular-nums font-medium text-gray-800 dark:text-gray-100">
                    {money(c.glBalance)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Detail says{c.detailCount ? ` (${c.detailCount})` : ''}
                  </p>
                  <p className="tabular-nums font-medium text-gray-800 dark:text-gray-100">
                    {money(c.subLedger)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Difference</p>
                  <p className={`tabular-nums font-semibold ${c.state === 'differs'
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-gray-500 dark:text-gray-400'}`}
                  >
                    {c.state === 'differs' ? money(c.difference) : '—'}
                  </p>
                </div>
              </div>

              {c.state === 'differs' && (
                <>
                  <p className="text-sm text-red-700 dark:text-red-300">{c.ifDiffers}</p>
                  {expandable && (
                    <button
                      type="button"
                      className={btnGhost}
                      onClick={() => setOpen(open === c.key ? null : c.key)}
                    >
                      {open === c.key ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      {open === c.key ? 'Hide' : 'Show the largest records behind it'}
                    </button>
                  )}
                  {open === c.key && (
                    <table className="w-full text-sm">
                      <tbody>
                        {c.contributors.map((x, i) => (
                          <tr key={`${x.reference ?? x.label}-${i}`} className="border-b border-gray-50 dark:border-gray-800">
                            <td className="py-1.5 pr-3">{x.label}</td>
                            <td className="py-1.5 pr-3 font-mono text-xs text-gray-400">{x.reference ?? ''}</td>
                            <td className="py-1.5 text-right tabular-nums">{money(x.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </>
              )}
            </div>
          </SoftCard>
        );
      })}
    </div>
  );
}
